import { fabric } from 'fabric';
import Canvas from "./../Canvas";
import CanvasObject from "./CanvasObject";
import apiRequestHelper from "./../../api/requestHelper";
import AppDebug from "./../../AppDebug";

class TextObject extends CanvasObject {

    static TYPE_TITLE = 'text';

    /**
     * Defines the font color.
     * @type string
     */
    _fill;
    _fillIsManipulable;
    _fillWhitelist;

    /**
     * The content of the text element.
     * @type string
     */
    _text;
    _textIsManipulable;
    _textWhitelist;
    _textSecondLanguage;

    /**
     * The font size in pixels.
     * @type number
     */
    _fontSize;
    _fontSizeIsManipulable;
    _fontSizeWhitelist;

    /**
     * The font family.
     * @type string
     */
    _fontFamily;
    _fontFamilyIsManipulable;
    _fontFamilyWhitelist;

    _label;
    _labelSecondLanguage;

    /**
     * If false, the object will not be saved. Allows the object to be saved only under conditions, for example after selecting a new image.
     * @type boolean
     */
    _print;

    _initText;

    /**
     * Create a TextObject.
     * @param {Canvas} canvas Instance of the canvas on which the object is displayed.
     * @param {string} content The content of the text element.
     */
    constructor(canvas, content) {
        super(canvas);

        this.setFillIsManipulable(true);
        this.setFillWhitelist(null);
        this.setTextIsManipulable(true);
        this.setTextWhitelist(null);
        this.setFontSizeIsManipulable(true);
        this.setFontSizeWhitelist(null);
        this.setFontFamilyIsManipulable(true);
        this.setFontFamilyWhitelist(null);
        this.setLabel(null);

        // Set content of text object and default values.
        this.initDefaultValues();

        // setter for properties must be manipulable first to set default values
        this.setText(content);
        this._initText = content;
        this.setFill('#000000');
        this.setFontSize(16);

        this._print = true;
    }

    /**
     * Adapt this instance to the new values of the matching FabricObject properties on the canvas.
     * @return fabric.Object
     */
    readFromFabricObject() {

        const fabricObject = super.readFromFabricObject();

        if (! fabricObject)
            return null;

        this.setFill( fabricObject.fill );
        this.setFontSize( fabricObject.fontSize );
        // this.setFontFamily( fabricObject.fontFamily );
        this.setText( fabricObject.text );

        return fabricObject;

    }

    /**
     * Returns a new FabricObject that can be added to the canvas.
     * @returns {Promise<fabric.Object>}
     */
    async createFabricObject() {

        if (this.getVisibility() === 2 && window['w2p_admin'] !== true)
            return null;

        const textFabricObject = new fabric.Text(this.getText(), {
            id: this.getId(),
            selectable: this.getIsSelectable() || window['w2p_admin'] === true,
            left: this.getLeft(),
            top: this.getTop(),
            fontSize: this.getFontSize(),
            fontFamily: this.getFontFamily() || 'Times New Roman',
            fill: this.getFill(),
            angle: this.getAngle(),
            scaleX: this.getScaleX(),
            scaleY: this.getScaleY(),
            textAlign: 'center',
            borderColor: this.getExceedsBoundingBox() ? 'red' : 'rgba(102,153,255,0.75)'
        });

        this.setHeight( textFabricObject.height );
        this.setWidth( textFabricObject.width );

        return textFabricObject;

    }

    /**
     * Object that is passed in JSON format to the API when saving the CanvasObject.
     * @returns {Object}
     */
    createObjectForSaveRequest() {

        return {
            isSelectable: this.getIsSelectable(),
            deletable: this.getDeletable(),
            label: {
                value: this.getLabel()
            },
            labelSecondLanguage: {
                value: this.getLabelSecondLanguage()
            },
            fill: {
                value: this.getFill(),
                manipulable: this.getFillIsManipulable(),
                whitelist: this.getFillWhitelist()
            },
            left: {
                value: this.getLeft(),
                manipulable: this.getLeftIsManipulable(),
                whitelist: this.getLeftWhitelist()
            },
            canvasRelativeLeft: {
                value: this.getLeftBoundingBox(),
                manipulable: this.getLeftBoundingBoxIsManipulable(),
                whitelist: this.getLeftBoundingBoxWhitelist()
            },
            top: {
                value: this.getTop(),
                manipulable: this.getTopIsManipulable(),
                whitelist: this.getTopWhitelist()
            },
            canvasRelativeTop: {
                value: this.getTopBoundingBox(),
                manipulable: this.getTopBoundingBoxIsManipulable(),
                whitelist: this.getTopBoundingBoxWhitelist()
            },
            width: {
                value: this.getWidth(),
                manipulable: this.getWidthIsManipulable(),
                whitelist: this.getWidthWhitelist()
            },
            height: {
                value: this.getHeight(),
                manipulable: this.getHeightIsManipulable(),
                whitelist: this.getHeightWhitelist()
            },
            angle: {
                value: this.getAngle(),
                manipulable: this.getAngleIsManipulable(),
                whitelist: this.getAngleWhitelist()
            },
            type: this.constructor.TYPE_TITLE,
            fontSize: {
                value: this.getFontSize(),
                manipulable: this.getFontSizeIsManipulable(),
                whitelist: this.getFontSizeWhitelist()
            },
            fontFamily: {
                value: this.getFontFamily() || 'Times New Roman',
                manipulable: this.getFontFamilyIsManipulable(),
                whitelist: this.getFontFamilyWhitelist()
            },
            text: {
                value: this.getText(),
                manipulable: this.getTextIsManipulable(),
                whitelist: this.getTextWhitelist()
            },
            print: this._print,
            image: null,
            scaleX: this.getScaleX(),
            scaleY: this.getScaleY(),
            visibility: this.getVisibility(),
            zPosition: this.getZPosition(),
            exceedsBoundingBox: this.getExceedsBoundingBox()
        };

    }

    /**
     * A post request is sent to the API and attempts to create the object.
     * @param {number} configurationViewId
     * @return {Promise<void>}
     */
    async save(configurationViewId) {

        if (this._print === false && window['w2p_admin'] !== true) {
            return;
        }

        this.readFromFabricObject();

        const image = await apiRequestHelper.requestApiRoute(
            'post',
            '/image',
            {
                image: await this.createBase64Image()
            });

        await apiRequestHelper.requestApiRoute(
            'post',
            '/object',
            {
                serializedObject: this.createObjectForSaveRequest(),
                productConfigurationViewId: configurationViewId,
                imageId: image.id
            },
            error => AppDebug.api.events.createCanvasObjectFailed({error})
            );

    }

    /**
     * Creates a image from the text element which is used to create the print file on the server.
     * @returns {Promise<string>}
     */
    async createBase64Image() {

        const object = this.getMatchingFabricObject();

        const sizes = object.getBoundingRect();

        // TODO no use of _factor, create getter
        // sizes.width = sizes.width * this.getCanvas()._factor;
        // sizes.height = sizes.height * this.getCanvas()._factor;

        const textClone = new fabric.Text(object.text, {
            left: 0,
            top: object.top - sizes.top,
            fill: object.fill,
            scaleX: 60,//object.scaleX,
            scaleY: 60,//object.scaleY,
            // angle: object.angle,
            fontSize: object.fontSize,
            fontFamily: object.fontFamily
        });

        const targetHeight = (this.getCanvas()._dpi * this.getScaledHeightInMm()) / 25.4;
        const targetWidth = (this.getCanvas()._dpi * this.getScaledWidthInMm()) / 25.4;

        console.log({
            scaleY: parseFloat(
                Number(targetHeight / this.getHeight()).toFixed(4)
            ),
            scaleX: parseFloat(
                Number(targetWidth / this.getWidth()).toFixed(4)
            ),
            currentHeight: this.getHeight(),
            targetHeight: targetHeight,
            currentWidth: this.getWidth(),
            targetWidth: targetWidth
        });

        textClone.set({
            scaleY: parseFloat(
                Number(targetHeight / this.getHeight()).toFixed(4)
            ),
            scaleX: parseFloat(
                Number(targetWidth / this.getWidth()).toFixed(4)
            )
        });

        const cloneSizes = textClone.getBoundingRect();

        textClone.set({
            top: textClone.top - cloneSizes.top,
            left: textClone.left - cloneSizes.left,
        });

        const pngCanvas = new fabric.Canvas(
            this.getCanvas().getCanvas().lowerCanvasEl.id.replace('canvas-', 'canvas-to-png-'),
            {
                width: targetWidth + (object.strokeWidth * 2),
                height: targetHeight + (object.strokeWidth * 2)
            }
        );

        pngCanvas.add(textClone);

        pngCanvas.renderAll();

        return pngCanvas.toDataURL();

        window.open(pngCanvas.toDataURL(),'_blank');

    }

    /**
     * Each type of object can define how its displayed in list of objects from current active product configuration view.
     * @returns {string} Filename
     */
    getCardTitleInObjectsConfigurator() {

        return {
            image: 'img/icon_type_text.png',
            title: `„${this.getText()}“`
        };

    }

    /**
     * Each type of object can define its own view in which controls are defined for the object.
     * @returns {string} Filename
     */
    getViewInObjectsConfigurator() {

        return 'TextCanvasObject.vue';

    }

    getFill() {
        return this._fill;
    }

    setFill(value) {
        if ( this.isManipulableAndWhitelistContainsValue('fill', value) === false )
            return;

        this._fill = value;
    }

    getLabel() {
        return this._label;
    }

    setLabel(value) {
        this._label = value;
    }

    getLabelSecondLanguage() {
        return this._labelSecondLanguage;
    }

    setLabelSecondLanguage(value) {
        this._labelSecondLanguage = value;
    }

    getFillIsManipulable() {
        return this._fillIsManipulable;
    }

    setFillIsManipulable(value) {
        this._fillIsManipulable = value;
    }

    getFillWhitelist() {
        return this._fillWhitelist;
    }

    setFillWhitelist(value) {
        this._fillWhitelist = value;
    }

    getText() {
        return this._text;
    }

    setText(value) {
        if ( this.isManipulableAndWhitelistContainsValue('text', value) === false )
            return;

        if (this._print === false && this._initText !== value) {
            this._print = true;
        }

        this._text = value;
    }

    getTextSecondLanguage() {
        return this._textSecondLanguage;
    }

    getTextIsManipulable() {
        return this._textIsManipulable;
    }

    setTextIsManipulable(value) {
        this._textIsManipulable = value;
    }

    getTextWhitelist() {
        return this._textWhitelist;
    }

    setTextWhitelist(value) {
        this._textWhitelist = value;
    }

    getFontSize() {
        return this._fontSize;
    }

    setFontSize(value) {
        if ( this.isManipulableAndWhitelistContainsValue('fontSize', value) === false )
            return;

        this._fontSize = value;
    }

    getFontSizeIsManipulable() {
        return this._fontSizeIsManipulable;
    }

    setFontSizeIsManipulable(value) {
        this._fontSizeIsManipulable = value;
    }

    getFontSizeWhitelist() {
        return this._fontSizeWhitelist;
    }

    setFontSizeWhitelist(value) {
        this._fontSizeWhitelist = value;
    }

    getFontFamily() {
        return this._fontFamily;
    }

    setFontFamily(value) {
        if ( this.isManipulableAndWhitelistContainsValue('fontFamily', value) === false )
            return;

        this._fontFamily = value;
    }

    getFontFamilyIsManipulable() {
        return this._fontFamilyIsManipulable;
    }

    setFontFamilyIsManipulable(value) {
        this._fontFamilyIsManipulable = value;
    }

    getFontFamilyWhitelist() {
        return this._fontFamilyWhitelist;
    }

    setFontFamilyWhitelist(value) {
        this._fontFamilyWhitelist = value;
    }

    isVisibleInSidebar() {

        if (
            super.isVisibleInSidebar() ||
            this.getTextIsManipulable() ||
            this.getFillIsManipulable() ||
            this.getFontFamilyIsManipulable()

        ) {
            return true;
        }

        return false;

    }

}

TextObject.createFromResponse = (response) => {
  return response;
};

export default TextObject;
